import React, { useEffect, useState, useCallback } from "react";
import { Modal, Input, Table, Button, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { http } from "../../services/http";
import moment from "moment";

const ViewAvailabilityReports = ({ visible, data, onClose, filters }) => {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(1000);
  const [hasMore, setHasMore] = useState(false);
  const [itemCode, setItemCode] = useState("");
  const [whsCode, setWhsCode] = useState("");
  const [resData, setResData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [whsData, setWhsData] = useState([]);

  const fetchData = async (itemCode, whsCode, currentPage) => {
    setLoading(true);
    try {
      let url = `api/dashboard/availability-report/details?whsCode=${whsCode}&itemCode=${itemCode}&skip=${currentPage}&pageSize=${pageSize}`;

      const { data } = await http.get(url);

      setHasMore(data.length === pageSize);

      setResData(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExcelLoading(true);
    let backUrl = `api/dashboard/availability-report/details/excel`;
    const body = {
      whsCode,
      itemCode,
      skip: 0,
      pageSize: 10000,
    };

    try {
      const res = await http.post(backUrl, body, {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob", // Ensure the response is treated as a blob
      });

      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/json",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        "Maxsulotlar bo'yicha harakatlar hisoboti.xlsx",
      ); // Define the download file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {}
    setExcelLoading(false);
  };

  const columns = [
    {
      title: t("itemName"),
      dataIndex: "itemName",
      key: "itemName",
    },
    {
      title: t("customer"),
      dataIndex: "customer",
      key: "customer",
    },
    {
      title: t("docNum"),
      dataIndex: "orderNumber",
      key: "orderNumber",
    },
    {
      title: t("orderDate"),
      dataIndex: "orderDate",
      key: "orderDate",
      render: (text) => {
        return moment(text).format("DD.MM.YYYY");
      },
    },
    {
      title: t("deliveryDate"),
      dataIndex: "deliveryDate",
      key: "deliveryDate",
      render: (text) => {
        return moment(text).format("DD.MM.YYYY");
      },
    },
    {
      title: t("docType"),
      dataIndex: "documentType",
      key: "documentType",
    },

    {
      title: t("fromWhs"),
      dataIndex: "warehouseName",
      key: "warehouseName",
    },
    {
      title: t("toWhs"),
      dataIndex: "destinationWarehouse",
      key: "destinationWarehouse",
    },
    {
      title: t("price"),
      dataIndex: "price",
      key: "price",
      render: (text, record) => {
        const formattedText = new Intl.NumberFormat("fr-FR").format(text);
        return `${formattedText} `;
      },
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      key: "quantity",
      render: (text, record) => {
        const formattedText = new Intl.NumberFormat("fr-FR").format(text);
        return `${formattedText} `;
      },
    },
    {
      title: t("reservedQuantity"),
      dataIndex: "reservedQuantity",
      key: "reservedQuantity",
      render: (text, record) => {
        const formattedText = new Intl.NumberFormat("fr-FR").format(text);
        return `${formattedText} `;
      },
    },

    {
      title: t("slp"),
      dataIndex: "salesperson",
      key: "salesperson",
    },
  ];

  useEffect(() => {
    if (data) {
      setItemCode(data.itemCode);
    }
    if (filters?.whsCode) {
      setWhsCode(filters.whsCode || "");
    } else {
      setWhsCode("");
    }
  }, [data, visible, filters]);

  useEffect(() => {
    fetchWarehouse();
  }, []);

  useEffect(() => {
    if (itemCode && visible) {
      fetchData(itemCode, whsCode, currentPage);
    }
  }, [currentPage, itemCode, visible, whsCode, filters]);

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const onCloseModal = () => {
    onClose();

    setCurrentPage(0);
    setHasMore(false);
    setResData([]);
  };

  const fetchWarehouse = useCallback(async () => {
    try {
      const { data } = await http.get(`api/warehouses`);

      setWhsData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);

  if (!data) return null;

  return (
    <Modal
      title={
        <h1 className="font-nunito text-sm font-extrabold text-[#000000]">
          {t("movementReportsByItem")} : {data.itemCode}
        </h1>
      }
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      closeIcon={<CloseOutlined />}
      width="95%"
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
    >
      <div className="w-full px-4">
        <div className="mt-10 flex items-center justify-end px-4">
          <Button
            onClick={handleExportExcel}
            loading={excelLoading}
            type="primary"
            className="ml-5 h-10 w-full rounded-lg bg-green-700 text-white sm:w-[200px]"
          >
            {t("exportExcel")}
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={resData}
          pagination={false}
          loading={loading}
          className="border-1 mt-5 w-full border-black"
          scroll={{ x: 1500 }}
        />
      </div>
    </Modal>
  );
};

export default ViewAvailabilityReports;
